import {
  Alert,
  Button,
  Drawer,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Typography,
} from 'antd';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useState } from 'react';

import type { CashKind, ClassOption, StudentOption } from './types';
import { formatMNT, KIND_LABEL, queryErrorMessage } from './types';

const { Text } = Typography;

// Records one Cash transaction. It can never be edited or deleted
// (CONTEXT.md), so the admin confirms the balance after it first.
export default function CashTransactionDrawer({
  kind,
  onClose,
  onDone,
}: {
  kind: CashKind;
  onClose: () => void;
  onDone: () => void;
}) {
  const { runQuery } = useScreen();
  const isDeposit = kind === 'DEPOSIT';

  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classId, setClassId] = useState<number>();
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentId, setStudentId] = useState<number>();
  const [amount, setAmount] = useState<number | null>(null);
  const [description, setDescription] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setClassesLoading(true);
    runQuery<ClassOption>('cash_class_options')
      .then(setClasses)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setClassesLoading(false));
  }, [runQuery]);

  useEffect(() => {
    setStudentId(undefined);
    setError(null);
    if (!classId) {
      setStudents([]);
      return;
    }
    setStudentsLoading(true);
    runQuery<StudentOption>('cash_class_students', { classId })
      .then(setStudents)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setStudentsLoading(false));
  }, [classId, runQuery]);

  const student = students.find((s) => s.id === studentId);
  const validAmount = amount != null && amount > 0 ? amount : null;
  const balanceAfter =
    student && validAmount != null
      ? student.card_balance + (isDeposit ? validAmount : -validAmount)
      : null;
  const belowMinimum =
    !isDeposit && student != null && balanceAfter != null && balanceAfter < student.min_balance;
  const needsDescription = !isDeposit && !description.trim();
  const canSubmit = !!student && validAmount != null && !belowMinimum && !needsDescription;

  const handleSubmit = () => {
    if (!student || validAmount == null) return;
    setSubmitting(true);
    setError(null);
    runQuery<{ id: number; balance_after: number }>('cash_transaction_create', {
      studentId: student.id,
      kind,
      amount: validAmount,
      description: description.trim() || null,
    })
      .then(() => {
        message.success(`${KIND_LABEL[kind]} бүртгэгдлээ.`);
        onDone();
      })
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setSubmitting(false));
  };

  const confirmDescription =
    student && validAmount != null && balanceAfter != null
      ? `${student.full_name}-ийн картад ${formatMNT(validAmount)} ${isDeposit ? 'нэмнэ' : 'хасна'}. Үлдэгдэл: ${formatMNT(student.card_balance)} → ${formatMNT(balanceAfter)}. Энэ гүйлгээг засах, устгах боломжгүй.`
      : '';

  return (
    <Drawer title={`${KIND_LABEL[kind]} бүртгэх`} open onClose={onClose} width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Select
          placeholder="Анги сонгох"
          loading={classesLoading}
          value={classId}
          onChange={(value) => setClassId(value)}
          allowClear
          onClear={() => setClassId(undefined)}
          showSearch
          optionFilterProp="label"
          options={classes.map((c) => ({ value: c.id, label: c.name }))}
        />

        <Select
          placeholder="Сурагч сонгох"
          loading={studentsLoading}
          disabled={!classId}
          value={studentId}
          onChange={(value) => setStudentId(value)}
          allowClear
          onClear={() => setStudentId(undefined)}
          showSearch
          optionFilterProp="label"
          options={students.map((s) => ({ value: s.id, label: `${s.full_name} (${s.code})` }))}
        />

        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
            Дүн
          </Text>
          <InputNumber<number>
            style={{ width: '100%' }}
            min={1}
            precision={0}
            value={amount}
            onChange={(value) => setAmount(value)}
            formatter={(value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => Number((value ?? '').replace(/,/g, ''))}
            addonAfter="₮"
          />
        </div>

        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
            Тайлбар{isDeposit ? '' : ' (заавал)'}
          </Text>
          <Input.TextArea
            rows={3}
            maxLength={500}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={isDeposit ? 'Бэлнээр цэнэглэсэн' : 'Ямар шалтгаанаар олгосон бэ?'}
          />
        </div>

        {student && (
          <div
            style={{
              borderRadius: 8,
              padding: 12,
              background: belowMinimum ? '#fff1f0' : '#f6ffed',
              border: `1px solid ${belowMinimum ? '#ffa39e' : '#b7eb8f'}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Картын үлдэгдэл</span>
              <strong>{formatMNT(student.card_balance)}</strong>
            </div>
            {balanceAfter != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span>Гүйлгээний дараа</span>
                <strong>{formatMNT(balanceAfter)}</strong>
              </div>
            )}
            {belowMinimum && (
              <p style={{ margin: '8px 0 0', color: '#cf1322', fontSize: 12 }}>
                Үлдэгдэл хамгийн бага зөвшөөрөгдөх {formatMNT(student.min_balance)}-өөс доош орно.
              </p>
            )}
          </div>
        )}

        {error && <Alert type="error" showIcon message={error} />}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button onClick={onClose} disabled={submitting}>
            Болих
          </Button>
          <Popconfirm
            title={`${KIND_LABEL[kind]} бүртгэх үү?`}
            description={confirmDescription}
            okText="Бүртгэх"
            okButtonProps={{ danger: !isDeposit }}
            cancelText="Болих"
            onConfirm={handleSubmit}
            disabled={!canSubmit}
          >
            <Button
              type="primary"
              danger={!isDeposit}
              disabled={!canSubmit || submitting}
              loading={submitting}
            >
              Бүртгэх
            </Button>
          </Popconfirm>
        </div>
      </div>
    </Drawer>
  );
}
